<script setup lang="ts" generic="ZodType extends z.ZodType<any>">
import { provide, useAttrs, useTemplateRef, watch } from 'vue'
import { useForm, useIsFormDirty, useIsFormTouched, useIsFormValid } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/zod'
import { z } from 'zod'

export interface UiFormProps<ZodType extends z.ZodType<any>> {
    schema?: ZodType
    scrollToFirstError?: boolean
    initialValues?: z.infer<ZodType>
    serverErrors?: Record<string, string | undefined>
}

const props = defineProps<UiFormProps<ZodType>>()
const attrs = useAttrs()
const emit = defineEmits<{
    (e: 'submit', values: z.infer<ZodType>): void
    (e: 'invalid-submit', errors: Record<string, string | undefined>): void
}>()

const formRef = useTemplateRef("formRef")

type FormValues = z.infer<ZodType>

const model = defineModel<z.infer<ZodType>>()
const isDirty = defineModel<boolean>('dirty', { default: false })
const isValid = defineModel<boolean>('valid', { default: false })
const wasTouched = defineModel<boolean>('touched', { default: false })

const form = useForm<FormValues>({
    validationSchema: props.schema ? toTypedSchema(props.schema) : undefined,
    initialValues: props.initialValues,
    validateOnMount: false,
})

const isFormDirty = useIsFormDirty();
watch(isFormDirty, (newVal) => {
    isDirty.value = newVal
}, { immediate: true })

const isFormValid = useIsFormValid();
watch(isFormValid, (newVal) => {
    isValid.value = newVal
}, { immediate: true })

const isFormTouched = useIsFormTouched();
watch(isFormTouched, (newVal) => {
    wasTouched.value = newVal
}, { immediate: true })

model.value = { ...form.values }

provide('UiForm:form', form)

watch(model, (newValues) => {
    if (!newValues) return
    form.setValues(newValues, false)
}, { deep: true })

watch(() => form.values, (newValues: FormValues) => {
    model.value = { ...newValues }
}, { deep: true })

watch(() => props.serverErrors, (errors) => {
    if (errors !== undefined) {
        form.setErrors(errors as any)
    }
}, { deep: true })

watch(() => form.errors.value, () => {
    // Re-apply any server errors that still exist in props but were cleared by validation
    if (props.serverErrors) {
        Object.keys(props.serverErrors).forEach(fieldName => {
            const serverError = props.serverErrors?.[fieldName]
            if (serverError && (form.errors.value as any)[fieldName] !== serverError) {
                form.setFieldError(fieldName as any, serverError)
            }
        })
    }
}, { deep: true })

function scrollToFirstError(errors: Record<string, string | undefined>) {
    const firstError = Object.keys(errors).find(key => errors[key as keyof typeof errors])
    if (firstError) {
        const errorElement = formRef.value?.querySelector(`[name="${firstError}"]`)
        if (!errorElement || !(errorElement instanceof HTMLElement)) return;
        errorElement.scrollIntoView({ behavior: 'smooth' })
        errorElement.focus()
    }
}

const onSubmit = form.handleSubmit((values: FormValues) => {
    emit('submit', values)
}, ({ errors }) => {
    emit('invalid-submit', errors)
    if (props.scrollToFirstError) {
        scrollToFirstError(errors)
    }
})

defineExpose({
    setErrors: form.setErrors,
    setFieldError: form.setFieldError,
    clearErrors: () => form.setErrors({}),
    validate: (shouldScrollToFirstError?: boolean) => {
        form.validate()
        if (shouldScrollToFirstError) {
            scrollToFirstError(form.errors.value)
        }
    },
    forceClear: () => {
        form.setErrors({})
        form.setTouched({})
    },
    resetForm: () => {
        form.resetForm()
        formRef.value?.reset()
    },
})
</script>

<template>
    <form v-bind="attrs" @submit.prevent="onSubmit" ref="formRef">
        <slot />
    </form>
    <slot name="debug" :meta="form.meta" :errors="form.errors" :values="form.values" />
</template>
